import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import FileUpload from '../components/FileUpload';
// WallFeedFileUpload removed with Wall Feed functionality
// MediaGallery components removed per user request
import FollowList from '../components/FollowList';
import FollowSuggestions from '../components/FollowSuggestions';
import UserInbox from '../components/UserInbox';
import MySubscription from '../components/MySubscription';
import {
  User, Save, Crown, 
  Badge as BadgeIcon, Star, Shield, Award,
  Image as ImageIcon, Archive, Heart, Book,
  Plus, Globe, ZoomIn, Upload, Camera,
  CreditCard, Download, Calendar, DollarSign, Play, Pause, Square,
  MessageSquare
} from 'lucide-react';
import paymentAPI from '../services/paymentApi';

// Import UI components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

// Import services
import { publicProfileAPI, tribalSocialAPI } from '../services/api';

const ProfileEdit = () => {
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  // Get the tab from URL parameters, default to 'story'
  const urlTab = searchParams.get('tab');
  const isRestricted = searchParams.get('restricted') === 'true';
  const [activeTab, setActiveTab] = useState(urlTab || 'story');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Media Photos state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState({});
  const [urlInput, setUrlInput] = useState('');
  const [uploadingUrl, setUploadingUrl] = useState(false);
  
  // Wall Feed functionality removed per user request
  // Media gallery functionality removed per user request
  
  // Ref for cleanup
  const abortControllerRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    // Profile Header
    profile_picture: '',
    tribal_name: '',
    legal_name: '',
    date_of_birth: '',
    region_flag: '',
    family_crest: '',
    family_crest_description: '',
    
    // My Story Bio Block
    bio_story: '',
    cultural_practices: '',
    oral_traditions: '',
    spiritual_totems: '',
    favorite_foods: '',
    languages_spoken: '',
    traditional_dances: '',
    medicinal_knowledge: '',
    
    // Social Media Links
    instagram_handle: '',
    twitter_handle: '',
    youtube_channel: '',
    facebook_url: '',
    twitch_url: '',
    
    // Media Photos
    media_photos: [], // Array to store uploaded photo URLs
    
    // Privacy Settings
    profile_privacy: 'public',
    show_genealogy: true,
    show_contact_info: false,
    allow_messages: true
  });

  const [badges] = useState([
    { id: 'verified_tribal', name: 'Verified Tribal Member', icon: Shield, color: 'bg-green-100 text-green-800' },
    { id: 'cultural_keeper', name: 'Cultural Keeper', icon: Crown, color: 'bg-purple-100 text-purple-800' },
    { id: 'storyteller', name: 'Oral Historian', icon: Star, color: 'bg-amber-100 text-amber-800' },
    { id: 'healer', name: 'Traditional Healer', icon: Award, color: 'bg-blue-100 text-blue-800' },
    { id: 'artisan', name: 'Master Artisan', icon: BadgeIcon, color: 'bg-orange-100 text-orange-800' }
  ]);

  const [regions] = useState([
    'Region 1 - Abenaki L\'Nabi',
    'Region 2 - Cherokee', 
    'Region 3 - Yamasee',
    'Region 4 - Chickasaw',
    'Region 5 - Kansa (Kaw)',
    'Region 6 - Choctaw',
    'Region 7 - Anasazi',
    'Region 8 - Hopi Zuni',
    'Region 9 - Kānaka Maoli',
    'Region 10 - Arawak (Taíno)',
    'Region 11 - Aztec',
    'Region 12 - Mayan',
    'Region 13 - Wayuu',
    'Region 14 - Goojijwininiwag',
    'Region 15 - Ojibway-Cree',
    'Region 16 - Sioux Tribe',
    'Region 17 - Blackfoot (Niitsítapi)',
    'Region 18 - The Cowichan Tribes',
    'Region 19 - Metis',
    'Region 20 - Haida',
    'Region 21 - Dogrib/Tåîchô',
    'Region 22 - Inuit',
    'Region 23 - Algonquin',
    'Region 24 - Mi\'kmaq'
  ]);

  // Subscription state
  const [subscription, setSubscription] = useState({
    plan_name: 'Crown Plan',
    status: 'active', // active | canceled | paused
    start_date: '2024-01-15',
    renewal_date: '2024-12-15',
    cost: '$9.99/month',
    genealogy_fee_paid: true,
    genealogy_fee_date: '2024-01-15',
    next_payment_date: '2024-12-15',
    payment_method: {
      type: 'card', // Only card payments supported
      last_four: '4242',
      brand: 'Visa'
    },
    billing_history: [
      { date: '2024-11-15', amount: '$9.99', status: 'paid', receipt_url: '#' },
      { date: '2024-10-15', amount: '$9.99', status: 'paid', receipt_url: '#' },
      { date: '2024-09-15', amount: '$9.99', status: 'paid', receipt_url: '#' },
      { date: '2024-01-15', amount: '$19.99', status: 'paid', description: 'Genealogy Activation Fee', receipt_url: '#' }
    ]
  });
  
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');

  // Payment handling functions
  const handleSubscriptionAction = async (action) => {
    setPaymentLoading(true);
    setSubscriptionMessage('');
    
    try {
      let result;
      switch (action) {
        case 'pause':
          result = await paymentAPI.pauseSubscription();
          setSubscription(prev => ({ ...prev, status: 'paused' }));
          setSubscriptionMessage('Subscription paused successfully. You can resume at any time.');
          break;
        case 'resume':
          result = await paymentAPI.resumeSubscription();
          setSubscription(prev => ({ ...prev, status: 'active' }));
          setSubscriptionMessage('Subscription resumed successfully!');
          break;
        case 'cancel':
          if (window.confirm('Are you sure you want to cancel your subscription? You can restart it later, but you\'ll lose access to premium features.')) {
            result = await paymentAPI.cancelSubscription();
            setSubscription(prev => ({ ...prev, status: 'canceled' }));
            setSubscriptionMessage('Subscription canceled. You can restart your subscription at any time.');
          }
          break;
        default:
          throw new Error('Invalid action');
      }
    } catch (error) {
      console.error(`Error ${action}ing subscription:`, error);
      setSubscriptionMessage(`Error ${action}ing subscription: ${error.message}`);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleStartSubscription = async () => {
    setPaymentLoading(true);
    setSubscriptionMessage('');
    
    try {
      const checkoutData = await paymentAPI.createCheckoutSession(
        paymentAPI.PaymentTypes.SUBSCRIPTION,
        paymentAPI.PlanIds.CROWN_PLAN
      );
      
      // Redirect to Stripe Checkout
      await paymentAPI.redirectToCheckout(checkoutData.session_id);
    } catch (error) {
      console.error('Error starting subscription:', error);
      setSubscriptionMessage(`Error starting subscription: ${error.message}`);
      setPaymentLoading(false);
    }
  };

  const handlePayGenealogyFee = async () => {
    setPaymentLoading(true);
    setSubscriptionMessage('');
    
    try {
      const checkoutData = await paymentAPI.createCheckoutSession(
        paymentAPI.PaymentTypes.GENEALOGY_FEE
      );
      
      // Redirect to Stripe Checkout
      await paymentAPI.redirectToCheckout(checkoutData.session_id);
    } catch (error) {
      console.error('Error processing genealogy fee:', error);
      setSubscriptionMessage(`Error processing payment: ${error.message}`);
      setPaymentLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setPaymentLoading(true);
    setSubscriptionMessage('');
    
    try {
      const result = await paymentAPI.createCustomerPortalSession();
      
      if (result.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = result.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      setSubscriptionMessage(`Error opening payment portal: ${error.message}`);
      setPaymentLoading(false);
    }
  };

  const handleDownloadReceipt = (receiptUrl) => {
    if (receiptUrl && receiptUrl !== '#') {
      window.open(receiptUrl, '_blank');
    } else {
      // Generate a sample receipt download
      const blob = new Blob(['Sample Receipt Content'], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'receipt.txt';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  };

  // Load subscription data on component mount
  useEffect(() => {
    if (isAuthenticated && user) {
      // Load real subscription data
      paymentAPI.getUserSubscription()
        .then(data => {
          if (data && !data.message) {
            // Update subscription state with real data
            setSubscription(prev => ({
              ...prev,
              status: data.status || prev.status,
              start_date: data.created_at || prev.start_date,
              genealogy_fee_paid: user.genealogy_fee_paid || prev.genealogy_fee_paid,
              genealogy_fee_date: user.genealogy_fee_date || prev.genealogy_fee_date
            }));
          }
        })
        .catch(error => {
          console.error('Error loading subscription:', error);
        });

      // Load payment history
      paymentAPI.getPaymentHistory()
        .then(data => {
          if (data && data.transactions) {
            const formattedHistory = data.transactions.map(t => ({
              date: t.created_at,
              amount: `$${t.amount.toFixed(2)}`,
              status: t.payment_status,
              description: t.payment_type === 'genealogy_fee' ? 'Genealogy Activation Fee' : 'Monthly Subscription',
              receipt_url: '#'
            }));
            setSubscription(prev => ({
              ...prev,
              billing_history: formattedHistory
            }));
          }
        })
        .catch(error => {
          console.error('Error loading payment history:', error);
        });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Wait for auth loading to complete before checking authentication
    if (authLoading) {
      console.log('ProfileEdit: Auth still loading, waiting...');
      return;
    }
    
    // Add debugging for authentication issues
    console.log('ProfileEdit Auth Check:', {
      isAuthenticated,
      authLoading,
      user: user?.email,
      userId: user?.id,
      hasToken: !!token,
      tokenLength: token?.length || 0
    });
    
    // More robust authentication check
    if (!isAuthenticated || !user?.id || !token) {
      console.log('ProfileEdit: User not properly authenticated, redirecting to home. Details:', {
        isAuthenticated,
        hasUser: !!user,
        hasUserId: !!user?.id,
        hasToken: !!token
      });
      navigate('/');
      return;
    }
    
    // Check if user was redirected due to restricted access
    if (isRestricted) {
      toast({
        title: "Access Restricted",
        description: "Your subscription is paused. You can only access your profile, community circles, and explore pages. Resume your subscription to regain full access.",
        variant: "destructive",
      });
    }
    
    console.log('ProfileEdit: User authenticated, loading profile');
    loadProfile();
    
    // Cleanup function to cancel any pending operations
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Wall Feed cleanup removed with functionality
    };
  }, [isAuthenticated, authLoading, navigate, isRestricted, toast]);

  // Use the authenticated user
  const currentUser = user;

  const loadProfile = async () => {
    if (!currentUser?.id) return;
    
    try {
      setLoading(true);
      const profile = await publicProfileAPI.getProfile(currentUser.id);
      const convertArrayToString = (value) => {
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return value || '';
      };

      setProfileData({
        profile_picture: profile.profile_picture || '',
        tribal_name: profile.tribal_name || '',
        legal_name: profile.legal_name || '',
        date_of_birth: profile.date_of_birth || '',
        region_flag: profile.region_flag || '',
        family_crest: profile.family_crest || '',
        family_crest_description: profile.family_crest_description || '',
        bio_story: profile.bio_story || '',
        // Convert arrays to comma-separated strings for editing
        cultural_practices: convertArrayToString(profile.cultural_practices),
        oral_traditions: convertArrayToString(profile.oral_traditions),
        spiritual_totems: convertArrayToString(profile.spiritual_totems),
        favorite_foods: convertArrayToString(profile.favorite_foods),
        languages_spoken: convertArrayToString(profile.languages_spoken),
        traditional_dances: convertArrayToString(profile.traditional_dances),
        medicinal_knowledge: convertArrayToString(profile.medicinal_knowledge),
        profile_privacy: profile.profile_privacy || 'public',
        show_genealogy: profile.show_genealogy !== false,
        show_contact_info: profile.show_contact_info || false,
        allow_messages: profile.allow_messages !== false,
        // Load media photos from profile or fetch from media API
        media_photos: profile.media_photos || []
      });

      // Also try to load media photos from the new media API
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/photos`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (response.ok) {
          const mediaData = await response.json();
          if (mediaData.success && mediaData.media_photos) {
            setProfileData(prev => ({
              ...prev,
              media_photos: mediaData.media_photos
            }));
          }
        }
      } catch (mediaError) {
        console.log('Could not load media photos from media API:', mediaError);
        // Non-critical error, continue with existing profile data
      }

    } catch (error) {
      console.error('Error loading profile:', error);
      
      // Don't show error for 404 (no profile yet) - just initialize empty profile
      if (error.response?.status === 404) {
        setProfileData({
          tribal_name: '',
          legal_name: '',
          date_of_birth: '',
          region_flag: '',
          profile_picture: '',
          family_crest: '',
          family_crest_description: '',
          bio_story: '',
          cultural_practices: '',
          oral_traditions: '',
          spiritual_totems: '',
          favorite_foods: '',
          languages_spoken: '',
          traditional_dances: '',
          medicinal_knowledge: '',
          profile_privacy: 'public',
          show_genealogy: true,
          show_contact_info: false,
          allow_messages: true,
          media_photos: []
        });
        console.log('No profile found - initialized empty profile');
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load profile data"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profileData.tribal_name?.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Tribal name is required"
      });
      return;
    }

    setSaving(true);
    try {
      // Prepare data for saving - convert strings to arrays for backend
      const convertStringToArray = (value) => {
        if (Array.isArray(value)) {
          return value;
        }
        if (typeof value === 'string' && value.trim()) {
          return value.split(',').map(item => item.trim()).filter(item => item);
        }
        return [];
      };

      // Convert media_photos to simple string array if it contains objects
      const convertMediaPhotosToUrls = (photos) => {
        if (!photos || !Array.isArray(photos)) return [];
        return photos.map(photo => {
          if (typeof photo === 'string') return photo;
          if (typeof photo === 'object' && photo.url) return photo.url;
          if (typeof photo === 'object' && photo.cloudinary_url) return photo.cloudinary_url;
          return null;
        }).filter(url => url);
      };

      const dataToSave = {
        ...profileData,
        // Ensure boolean fields are properly converted
        show_genealogy: Boolean(profileData.show_genealogy),
        show_contact_info: Boolean(profileData.show_contact_info),
        allow_messages: Boolean(profileData.allow_messages),
        // Ensure profile_privacy is a valid enum value
        profile_privacy: profileData.profile_privacy || 'public', // Must be: 'public', 'tribe_only', 'private'
        // Convert comma-separated strings to arrays for backend
        cultural_practices: convertStringToArray(profileData.cultural_practices),
        languages_spoken: convertStringToArray(profileData.languages_spoken),
        spiritual_totems: convertStringToArray(profileData.spiritual_totems),
        traditional_dances: convertStringToArray(profileData.traditional_dances),
        favorite_foods: convertStringToArray(profileData.favorite_foods),
        oral_traditions: convertStringToArray(profileData.oral_traditions),
        medicinal_knowledge: convertStringToArray(profileData.medicinal_knowledge),
        // Convert media photos to simple URL strings
        media_photos: convertMediaPhotosToUrls(profileData.media_photos),
        // Remove any fields that aren't in the PublicProfileRequest model
        _forceUpdate: undefined, // Remove internal React state field
      };

      // Remove undefined fields to avoid validation errors
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === undefined) {
          delete dataToSave[key];
        }
      });

      console.log('Saving profile data:', dataToSave);
      await publicProfileAPI.updateProfile(dataToSave);
      
      toast({
        title: "Success!",
        description: "Profile saved successfully"
      });
      
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: error.response?.data?.message || error.message || "Failed to save profile. Please try again."
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  // For array fields, we'll store them as strings during editing and convert to arrays when saving
  const handleArrayInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  // Media photo upload functions (CLOUDINARY ONLY)
  const handleMediaPhotoUpload = async (file) => {
    if (!file) return;

    // Validate file size (15MB max)
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Please select an image under 15MB"
      });
      return;
    }

    // Check if we already have 10 photos
    if ((profileData.media_photos || []).length >= 10) {
      toast({
        variant: "destructive",
        title: "Photo limit reached",
        description: "Maximum 10 photos allowed"
      });
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select a JPG, PNG, or WEBP image"
      });
      return;
    }

    setUploadingPhoto(true);
    const photoId = Date.now().toString();
    
    try {
      setPhotoUploadProgress(prev => ({ ...prev, [photoId]: 0 }));

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('files', file); // Use 'files' for Cloudinary media upload (expects array)
      
      // Use CLOUDINARY media upload endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      console.log('Upload response:', result); // Debug log
      
      if (result.success && result.uploaded_photos && result.uploaded_photos.length > 0) {
        // Handle Cloudinary format
        const newPhotos = result.uploaded_photos.map(photo => ({
          url: photo.cloudinary_url || photo.url,
          public_id: photo.cloudinary_public_id || photo.public_id,
          filename: photo.original_filename || photo.filename,
          size: photo.file_size || photo.size,
          upload_timestamp: photo.upload_timestamp || new Date().toISOString()
        }));

        setProfileData(prev => ({
          ...prev,
          media_photos: [...(prev.media_photos || []), ...newPhotos]
        }));

        toast({
          title: "Photo uploaded!",
          description: `Successfully uploaded ${result.uploaded_count} photo(s) to your media gallery`
        });
      } else {
        console.error('Upload failed:', result); // Debug log
        throw new Error(result.message || result.detail || 'Upload failed - please check file format and size');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      
      // Properly handle error object for display
      let errorMessage = "Failed to upload photo. Please try again.";
      if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        } else {
          errorMessage = JSON.stringify(error);
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: errorMessage
      });
    } finally {
      setUploadingPhoto(false);
      setPhotoUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[photoId];
        return newState;
      });
    }
  };

  // Media photo URL upload function
  const handleMediaPhotoUrlUpload = async () => {
    if (!urlInput.trim()) return;

    // Validate URL format
    let url;
    try {
      url = new URL(urlInput);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid protocol');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Invalid URL",
        description: "Please enter a valid HTTP or HTTPS URL"
      });
      return;
    }

    // Check if we already have 10 photos
    if ((profileData.media_photos || []).length >= 10) {
      toast({
        variant: "destructive",
        title: "Photo limit reached",
        description: "Maximum 10 photos allowed"
      });
      return;
    }

    setUploadingUrl(true);
    
    try {
      // Add the URL photo to the media_photos array (simulating upload)
      const newPhoto = {
        url: urlInput.trim(),
        filename: `URL Photo ${Date.now()}`,
        upload_timestamp: new Date().toISOString(),
        source: 'url'
      };

      setProfileData(prev => ({
        ...prev,
        media_photos: [...(prev.media_photos || []), newPhoto]
      }));

      setUrlInput(''); // Clear the input
      
      toast({
        title: "Photo added!",
        description: "Photo URL has been added to your media gallery"
      });
    } catch (error) {
      console.error('Error adding photo URL:', error);
      toast({
        variant: "destructive",
        title: "Failed to add photo",
        description: error.message || "Failed to add photo URL. Please try again."
      });
    } finally {
      setUploadingUrl(false);
    }
  };

  const removeMediaPhoto = async (photo, index) => {
    try {
      console.log('Removing photo:', photo); // Debug log
      
      // Try to delete from Cloudinary backend if it has a cloudinary public_id
      const publicId = photo.cloudinary_public_id || photo.public_id;
      
      if (publicId) {
        console.log('Attempting to delete from server with public_id:', publicId);
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/photo/${encodeURIComponent(publicId)}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Server delete failed:', errorData);
          
          // Continue with local deletion even if server delete fails
          console.log('Server delete failed, but continuing with local deletion');
        } else {
          const result = await response.json();
          console.log('Server delete result:', result);
        }
      } else {
        console.log('No public_id found, skipping server deletion');
      }

      // Always remove from local state (whether server deletion succeeded or not)
      const updatedPhotos = (profileData.media_photos || []).filter((_, i) => i !== index);
      
      setProfileData(prev => ({
        ...prev,
        media_photos: updatedPhotos
      }));
      
      // Also save the updated photos list to the database
      try {
        const saveResult = await updateProfileInDatabase({ media_photos: updatedPhotos });
        console.log('Updated profile with new photos list:', saveResult);
      } catch (saveError) {
        console.error('Failed to save updated photos to database:', saveError);
        // Don't show error to user since local deletion worked
      }
      
      toast({
        title: "Photo removed",
        description: "Photo has been removed from your media gallery"
      });
      
      console.log('Photo removed from local state successfully');
      
    } catch (error) {
      console.error('Error removing photo:', error);
      
      // Even if there's an error, try to remove from local state
      try {
        setProfileData(prev => ({
          ...prev,
          media_photos: (prev.media_photos || []).filter((_, i) => i !== index)
        }));
        
        toast({
          title: "Photo removed locally",
          description: "Photo removed from display (server deletion may have failed)"
        });
      } catch (localError) {
        console.error('Failed to remove from local state:', localError);
        toast({
          variant: "destructive",
          title: "Delete failed",
          description: error.message || "Failed to remove photo. Please try again."
        });
      }
    }
  };

  const moveMediaPhoto = (fromIndex, toIndex) => {
    const photos = [...(profileData.media_photos || [])];
    const [movedPhoto] = photos.splice(fromIndex, 1);
    photos.splice(toIndex, 0, movedPhoto);
    
    setProfileData(prev => ({
      ...prev,
      media_photos: photos
    }));
  };

  const replaceMediaPhoto = async (index, file) => {
    if (!file) return;

    // Validate file
    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Please select an image under 15MB"
      });
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select a JPG, PNG, or WEBP image"
      });
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append('files', file); // Use 'files' for Cloudinary media endpoint
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.uploaded_photos && result.uploaded_photos[0]) {
        const uploadedPhoto = result.uploaded_photos[0];
        const newPhoto = {
          url: uploadedPhoto.cloudinary_url || uploadedPhoto.url,
          public_id: uploadedPhoto.cloudinary_public_id || uploadedPhoto.public_id,
          filename: uploadedPhoto.original_filename || uploadedPhoto.filename,
          size: uploadedPhoto.file_size || uploadedPhoto.size,
          upload_timestamp: uploadedPhoto.upload_timestamp || new Date().toISOString()
        };
        
        const newPhotos = [...(profileData.media_photos || [])];
        newPhotos[index] = newPhoto;
        
        setProfileData(prev => ({
          ...prev,
          media_photos: newPhotos
        }));

        toast({
          title: "Photo replaced!",
          description: "Photo has been successfully replaced"
        });
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error replacing photo:', error);
      toast({
        variant: "destructive",
        title: "Replace failed",
        description: error.message || "Failed to replace photo. Please try again."
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Profile Photo Upload Function (CLOUDINARY ONLY)
  const handleProfilePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Invalid File Type",
        description: "Please upload a JPG, PNG, or WEBP image."
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: "Please upload an image smaller than 10MB."
      });
      return;
    }

    setLoading(true);
    
    try {
      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file); // Single file for profile photo

      // Upload using CLOUDINARY profile photo endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/profile-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success && result.data && result.data.url) {
        const photoUrl = result.data.url;
        
        console.log('Setting profile photo URL:', photoUrl);
        
        // Update profile data with new photo URL - Force re-render
        setProfileData(prev => {
          const updated = {
            ...prev,
            profile_picture: photoUrl,
            _forceUpdate: Date.now() // Force React to recognize this as a change
          };
          console.log('Updated profile data:', updated);
          return updated;
        });
        
        // Save to backend immediately
        const saveResult = await updateProfileInDatabase({ profile_picture: photoUrl });
        console.log('Save result:', saveResult);

        toast({
          title: "Success!",
          description: "Profile photo uploaded successfully!"
        });
      } else {
        throw new Error(result.message || result.detail || 'Upload failed - no URL received');
      }

    } catch (error) {
      console.error('Profile photo upload error:', error);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error.message || "Failed to upload profile photo. Please try again."
      });
    } finally {
      setLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  // Function to update profile in database
  const updateProfileInDatabase = async (updates) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public-profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error(`Update failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Profile update error:', error);
      // Don't show error toast for background saves
      return null;
    }
  };

  // Wall Feed functions removed per user request

  // Show loading while auth is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-100 to-orange-100 border-b">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Edit Your Sovereign Identity</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Share your cultural heritage and personal story</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="text-xs sm:text-sm">
                Cancel
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate(`/public-profile/${currentUser?.id}`)}
                className="border-blue-200 text-blue-700 hover:bg-blue-50 text-xs sm:text-sm"
              >
                <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                View Public Profile
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/support-ticket')}
                className="border-amber-200 text-amber-700 hover:bg-amber-50 text-xs sm:text-sm hidden sm:flex"
              >
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Support Tickets
              </Button>
              <Button onClick={handleSave} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm">
                <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap w-full gap-1 h-auto p-1 sm:grid sm:grid-cols-6">
            <TabsTrigger value="story" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">My Story</TabsTrigger>
            <TabsTrigger value="followers" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">Followers</TabsTrigger>
            <TabsTrigger value="following" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">Following</TabsTrigger>
            <TabsTrigger value="inbox" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">Messages</TabsTrigger>
            <TabsTrigger value="subscription" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">Subscription</TabsTrigger>
            <TabsTrigger value="privacy" className="text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 whitespace-nowrap">Settings</TabsTrigger>
          </TabsList>

          {/* My Story Tab */}
          <TabsContent value="story" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>My Story</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Photo Upload Section */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold flex items-center">
                    <Camera className="w-5 h-5 mr-2" />
                    Profile Photo
                  </Label>
                  
                  <div className="flex flex-col lg:flex-row items-start gap-6">
                    {/* Profile Photo Preview - Fixed Version */}
                    <div className="flex-shrink-0 relative">
                      <div className="h-32 w-32 border-4 border-gray-200 rounded-full overflow-hidden bg-gray-100">
                        {profileData.profile_picture ? (
                          <img
                            key={profileData.profile_picture}
                            src={profileData.profile_picture}
                            alt="Profile photo"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Profile image failed to load:', profileData.profile_picture);
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              console.log('Profile image loaded successfully:', profileData.profile_picture);
                              e.target.nextSibling.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-full h-full flex items-center justify-center text-2xl text-gray-600"
                          style={{ display: profileData.profile_picture ? 'none' : 'flex' }}
                        >
                          {profileData.tribal_name?.charAt(0) || 
                           profileData.legal_name?.charAt(0) || 
                           'C'}
                        </div>
                      </div>
                      
                      {/* Debug info */}
                      {profileData.profile_picture && (
                        <div className="text-xs text-gray-500 mt-1 max-w-32 truncate">
                          URL: {profileData.profile_picture}
                        </div>
                      )}
                      
                      {/* Upload Button Overlay */}
                      <label
                        htmlFor="profile-photo-input"
                        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-50 transition-all duration-200 cursor-pointer rounded-full"
                      >
                        <div className="bg-white p-2 rounded-full shadow-lg opacity-0 hover:opacity-100 transition-opacity duration-200">
                          <Camera className="w-5 h-5 text-gray-700" />
                        </div>
                      </label>
                    </div>

                    {/* Upload Instructions and Button */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Upload Your Profile Photo</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Choose a clear photo that represents you. This will appear on your profile and throughout the community.
                        </p>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          <span>Recommended: Square format (1:1 ratio)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          <span>Supported: JPG, PNG, WEBP</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          <span>Maximum size: 10MB</span>
                        </div>
                      </div>

                      {/* Upload Button */}
                      <div>
                        <input
                          type="file"
                          id="profile-photo-input"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleProfilePhotoUpload}
                          className="hidden"
                          disabled={loading}
                        />
                        <label
                          htmlFor="profile-photo-input"
                          className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm font-medium ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Choose Photo
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bio Story Section */}
                <div>
                  <Label htmlFor="bio_story">Tell Your Story</Label>
                  <Textarea
                    id="bio_story"
                    value={profileData.bio_story}
                    onChange={(e) => handleInputChange('bio_story', e.target.value)}
                    placeholder="Share your cultural heritage, family history, and personal journey..."
                    className="mt-1 min-h-[120px]"
                    maxLength={2000}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {profileData.bio_story.length}/2000 characters
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Names */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="tribal_name">Tribal Name *</Label>
                    <Input
                      id="tribal_name"
                      value={profileData.tribal_name}
                      onChange={(e) => handleInputChange('tribal_name', e.target.value)}
                      placeholder="e.g., Red Hawk of Yamasee"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Your Indigenous name or tribal identity</p>
                  </div>
                  <div>
                    <Label htmlFor="legal_name">Legal/Status Name</Label>
                    <Input
                      id="legal_name"
                      value={profileData.legal_name}
                      onChange={(e) => handleInputChange('legal_name', e.target.value)}
                      placeholder="For private notes or documents"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional: Legal name for documentation</p>
                  </div>
                </div>

                {/* Date of Birth & Region */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={profileData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="region_flag">Region or Clan Flag</Label>
                    <Select value={profileData.region_flag} onValueChange={(value) => handleInputChange('region_flag', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select your region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map(region => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Family Crest */}
                <div>
                  <Label htmlFor="family_crest">Family Crest</Label>
                  <div className="mt-2 space-y-4">
                    <FileUpload 
                      uploadType="family_crest"
                      currentValue={profileData.family_crest}
                      onUploadSuccess={async (result) => {
                        // Update local state
                        const updatedProfileData = {
                          ...profileData,
                          family_crest: result.url
                        };
                        setProfileData(updatedProfileData);
                        
                        // Auto-save profile with updated data
                        try {
                          await publicProfileAPI.updateProfile(updatedProfileData);
                          toast({
                            title: "Success!",
                            description: "Family crest uploaded and saved successfully"
                          });
                        } catch (error) {
                          console.error('Error auto-saving profile:', error);
                          toast({
                            variant: "destructive",
                            title: "Upload succeeded but save failed",
                            description: "Crest uploaded but couldn't save to profile. Please click Save Profile."
                          });
                        }
                      }}
                    />
                    <div className="flex gap-3">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => navigate('/crest')}
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Generate Crest
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Upload existing crest or create using our Crest Generator</p>
                  
                  {/* Family Crest Description */}
                  <div className="mt-4">
                    <Label htmlFor="family_crest_description">Family Crest Description</Label>
                    <Textarea
                      id="family_crest_description"
                      value={profileData.family_crest_description}
                      onChange={(e) => handleInputChange('family_crest_description', e.target.value)}
                      placeholder="Describe the meaning, symbols, and history of your family crest..."
                      className="mt-1 min-h-[100px]"
                      maxLength={1000}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {profileData.family_crest_description.length}/1000 characters
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cultural Heritage Information */}
            <Card>
              <CardHeader>
                <CardTitle>Cultural Heritage & Traditions</CardTitle>
                <p className="text-sm text-gray-600">Share your cultural practices, spiritual beliefs, traditional foods, and dances</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cultural Practices */}
                <div>
                  <Label htmlFor="cultural_practices">Cultural Practices</Label>
                  <Textarea
                    id="cultural_practices"
                    value={profileData.cultural_practices || ''}
                    onChange={(e) => handleArrayInputChange('cultural_practices', e.target.value)}
                    placeholder="e.g., Smudging ceremonies, Traditional storytelling, Drumming circles, Beadwork, Pottery making"
                    className="mt-1 min-h-[80px]"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate multiple practices with commas. Traditional ceremonies, crafts, or cultural activities you practice.
                  </p>
                </div>

                {/* Spiritual Totems */}
                <div>
                  <Label htmlFor="spiritual_totems">Spiritual Totems & Guides</Label>
                  <Textarea
                    id="spiritual_totems"
                    value={profileData.spiritual_totems || ''}
                    onChange={(e) => handleArrayInputChange('spiritual_totems', e.target.value)}
                    placeholder="e.g., Eagle, Bear, Wolf, Turtle, Butterfly, Cedar, Sage, Thunder beings"
                    className="mt-1 min-h-[80px]"
                    maxLength={300}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate multiple totems with commas. Animal spirits, plant allies, or spiritual guides that are meaningful to you.
                  </p>
                </div>

                {/* Traditional Foods */}
                <div>
                  <Label htmlFor="favorite_foods">Traditional Foods</Label>
                  <Textarea
                    id="favorite_foods"
                    value={profileData.favorite_foods || ''}
                    onChange={(e) => handleArrayInputChange('favorite_foods', e.target.value)}
                    placeholder="e.g., Frybread, Wild rice, Salmon, Three Sisters (corn, beans, squash), Pemmican, Acorn meal, Buffalo"
                    className="mt-1 min-h-[80px]"
                    maxLength={400}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate multiple foods with commas. Traditional dishes, ingredients, or ceremonial foods important to your heritage.
                  </p>
                </div>

                {/* Traditional Dances */}
                <div>
                  <Label htmlFor="traditional_dances">Traditional Dances</Label>
                  <Textarea
                    id="traditional_dances"
                    value={profileData.traditional_dances || ''}
                    onChange={(e) => handleArrayInputChange('traditional_dances', e.target.value)}
                    placeholder="e.g., Powwow dances, Stomp dance, Hoop dance, Grass dance, Jingle dress, Round dance, Rain dance"
                    className="mt-1 min-h-[80px]"
                    maxLength={400}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate multiple dances with commas. Traditional dances you know, participate in, or are learning.
                  </p>
                </div>

                {/* Additional Cultural Information */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Star className="w-5 h-5 text-amber-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-900 mb-1">Cultural Sharing</h4>
                      <p className="text-sm text-amber-700">
                        Share your cultural knowledge to help preserve and celebrate Indigenous traditions. 
                        This information helps build connections with others who share similar practices and heritage.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* My Photo Gallery Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ImageIcon className="w-5 h-5 mr-2" />
                  My Photo Gallery
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Share photos of your cultural heritage, family, ceremonies, and traditions
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upload Section */}
                <div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-400 transition-colors">
                    <div className="space-y-4">
                      <ImageIcon className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Upload Photos to Gallery</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Share up to 10 photos of your cultural heritage, ceremonies, family, and traditions
                        </p>
                        
                        <div className="space-y-2 text-sm text-gray-500 mb-4">
                          <div className="flex items-center justify-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span>Maximum 10 photos ({10 - (profileData.media_photos || []).length} remaining)</span>
                          </div>
                          <div className="flex items-center justify-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span>Maximum 15MB per photo</span>
                          </div>
                          <div className="flex items-center justify-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <span>Supported formats: JPG, PNG, WEBP</span>
                          </div>
                        </div>

                        <input
                          type="file"
                          id="media-photo-upload"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleMediaPhotoUpload(e.target.files[0]);
                              e.target.value = ''; // Reset input
                            }
                          }}
                          className="hidden"
                          disabled={uploadingPhoto || (profileData.media_photos || []).length >= 10}
                        />
                        
                        <label
                          htmlFor="media-photo-upload"
                          className={`inline-flex items-center gap-2 px-6 py-3 ${
                            uploadingPhoto || (profileData.media_photos || []).length >= 10
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : 'bg-amber-600 hover:bg-amber-700 cursor-pointer'
                          } text-white rounded-lg transition-colors font-medium`}
                        >
                          {uploadingPhoto ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              Choose Photo
                            </>
                          )}
                        </label>
                        
                        {/* URL Upload Section */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">Or add photo by URL</h5>
                          <div className="flex gap-2">
                            <input
                              type="url"
                              value={urlInput}
                              onChange={(e) => setUrlInput(e.target.value)}
                              placeholder="https://example.com/photo.jpg"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              disabled={uploadingUrl || (profileData.media_photos || []).length >= 10}
                            />
                            <button
                              type="button"
                              onClick={handleMediaPhotoUrlUpload}
                              disabled={uploadingUrl || !urlInput.trim() || (profileData.media_photos || []).length >= 10}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                uploadingUrl || !urlInput.trim() || (profileData.media_photos || []).length >= 10
                                  ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                            >
                              {uploadingUrl ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </>
                              ) : (
                                'Add URL'
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Enter a direct link to an image (JPG, PNG, WEBP)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Photo Gallery */}
                {(profileData.media_photos || []).length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Your Photo Gallery ({(profileData.media_photos || []).length}/10)</h5>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {(profileData.media_photos || []).map((photo, index) => {
                        // Handle both old URL format and new photo object format
                        const photoUrl = typeof photo === 'string' ? photo : photo.url;
                        const photoFilename = typeof photo === 'string' ? `Photo ${index + 1}` : photo.filename;
                        
                        return (
                          <div key={index} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-transparent group-hover:border-amber-300 transition-all">
                              <img
                                src={photoUrl}
                                alt={`Media photo ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  console.error('Image failed to load:', photoUrl);
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgODBMMTIwIDEwMEgxMDBWMTIwSDgwVjEwMEgxMDBWODBaIiBmaWxsPSIjOUNBM0FGIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTQwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNjc3NDhGIiBmb250LXNpemU9IjE0Ij5JbWFnZSBub3QgZm91bmQ8L3RleHQ+Cjwvc3ZnPg==';
                                  e.target.classList.add('opacity-75');
                                }}
                                onLoad={(e) => {
                                  e.target.classList.remove('opacity-75');
                                }}
                              />
                            </div>
                            
                            {/* Photo Controls */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                {/* Move Up */}
                                {index > 0 && (
                                  <button
                                    onClick={() => moveMediaPhoto(index, index - 1)}
                                    className="w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold"
                                    title="Move up"
                                  >
                                    ↑
                                  </button>
                                )}
                                
                                {/* Replace Photo */}
                                <label className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center cursor-pointer text-xs" title="Replace photo">
                                  ↻
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp"
                                    onChange={(e) => {
                                      if (e.target.files[0]) {
                                        replaceMediaPhoto(index, e.target.files[0]);
                                        e.target.value = '';
                                      }
                                    }}
                                    className="hidden"
                                    disabled={uploadingPhoto}
                                  />
                                </label>
                                
                                {/* Remove Photo */}
                                <button
                                  onClick={() => removeMediaPhoto(photo, index)}
                                  className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold"
                                  title="Remove photo"
                                >
                                  ✕
                                </button>
                                
                                {/* Move Down */}
                                {index < (profileData.media_photos || []).length - 1 && (
                                  <button
                                    onClick={() => moveMediaPhoto(index, index + 1)}
                                    className="w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold"
                                    title="Move down"
                                  >
                                    ↓
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Photo Info */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="text-white text-xs">
                                <div className="font-medium truncate">{photoFilename}</div>
                                {typeof photo === 'object' && photo.size && (
                                  <div className="text-gray-300">
                                    {(photo.size / (1024 * 1024)).toFixed(2)}MB
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Photo Number */}
                            <div className="absolute top-2 left-2 w-6 h-6 bg-black bg-opacity-70 text-white text-xs rounded-full flex items-center justify-center">
                              {index + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Management Instructions */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Photo Management:</strong> Hover over photos to move, replace, or remove them. Photos appear on your public profile in this order.
                      </p>
                    </div>
                  </div>
                )}

                {/* Information note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <ImageIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900 mb-1">Photo Guidelines</h4>
                      <p className="text-sm text-blue-700">
                        Share photos that represent your cultural heritage, family traditions, ceremonies, or meaningful moments. 
                        These photos will be displayed on your public profile to help others learn about and connect with your story.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Media Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Globe className="w-5 h-5 mr-2" />
                  Social Media & Online Presence
                </CardTitle>
                <p className="text-sm text-gray-600">Connect your social media accounts to share your cultural journey and connect with the community</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Instagram Handle */}
                <div>
                  <Label htmlFor="instagram_handle" className="flex items-center">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="currentColor" strokeWidth="2"/>
                      <path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="currentColor" strokeWidth="2"/>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Instagram Handle
                  </Label>
                  <div className="flex items-center mt-1">
                    <span className="text-gray-500 text-sm mr-2">@</span>
                    <Input
                      id="instagram_handle"
                      value={profileData.instagram_handle}
                      onChange={(e) => handleInputChange('instagram_handle', e.target.value)}
                      placeholder="your_instagram_username"
                      className="flex-1"
                      maxLength={30}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your Instagram username without the @ symbol. This will be displayed as a clickable link on your profile.
                  </p>
                </div>

                {/* Twitter Handle */}
                <div>
                  <Label htmlFor="twitter_handle" className="flex items-center">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Twitter Handle
                  </Label>
                  <div className="flex items-center mt-1">
                    <span className="text-gray-500 text-sm mr-2">@</span>
                    <Input
                      id="twitter_handle"
                      value={profileData.twitter_handle}
                      onChange={(e) => handleInputChange('twitter_handle', e.target.value)}
                      placeholder="your_twitter_handle"
                      className="flex-1"
                      maxLength={15}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your Twitter/X username without the @ symbol. Share your thoughts and connect with the Indigenous community.
                  </p>
                </div>

                {/* YouTube Channel */}
                <div>
                  <Label htmlFor="youtube_channel" className="flex items-center">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" stroke="currentColor" strokeWidth="2"/>
                      <polygon points="9.75,15.02 15.5,11.75 9.75,8.48" fill="currentColor"/>
                    </svg>
                    YouTube Channel
                  </Label>
                  <Input
                    id="youtube_channel"
                    value={profileData.youtube_channel}
                    onChange={(e) => handleInputChange('youtube_channel', e.target.value)}
                    placeholder="https://youtube.com/@yourchannel or youtube.com/c/YourChannelName"
                    className="mt-1"
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your full YouTube channel URL. Share your cultural content, tutorials, stories, and traditional knowledge.
                  </p>
                </div>

                {/* Facebook URL */}
                <div>
                  <Label htmlFor="facebook_url" className="flex items-center">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="currentColor"/>
                    </svg>
                    Facebook Profile/Page
                  </Label>
                  <Input
                    id="facebook_url"
                    value={profileData.facebook_url}
                    onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                    placeholder="https://facebook.com/yourprofile or facebook.com/yourpage"
                    className="mt-1"
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your Facebook profile or page URL. Connect with family, share cultural events, and build community.
                  </p>
                </div>

                {/* Twitch URL */}
                <div>
                  <Label htmlFor="twitch_url" className="flex items-center">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" fill="currentColor"/>
                    </svg>
                    Twitch Channel
                  </Label>
                  <Input
                    id="twitch_url"
                    value={profileData.twitch_url}
                    onChange={(e) => handleInputChange('twitch_url', e.target.value)}
                    placeholder="https://twitch.tv/yourchannel or twitch.tv/yourname"
                    className="mt-1"
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your Twitch channel URL. Stream cultural content, gaming, art, and connect with the community.
                  </p>
                </div>

                {/* Social Media Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Globe className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900 mb-1">Share Your Cultural Voice</h4>
                      <p className="text-sm text-blue-700">
                        Use your social media platforms to educate, inspire, and preserve Indigenous culture. 
                        These links will appear on your public profile and help others find your content across platforms.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Followers Tab */}
          <TabsContent value="followers" className="space-y-6">
            <FollowList 
              listType="followers"
              showTitle={true}
              limit={20}
            />
            
            {/* Follow Suggestions */}
            <FollowSuggestions 
              limit={5}
              showTitle={true}
              className="mt-6"
            />
          </TabsContent>

          {/* Following Tab */}
          <TabsContent value="following" className="space-y-6">
            <FollowList 
              listType="following"
              showTitle={true}
              limit={20}
            />
          </TabsContent>

          {/* Messages/Inbox Tab */}
          <TabsContent value="inbox" className="space-y-6">
            <UserInbox conversationId={searchParams.get('conversation')} />
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription" className="space-y-6">
            <MySubscription user={user} />
          </TabsContent>

          {/* Privacy & Settings Tab */}
          <TabsContent value="privacy" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Privacy Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="profile_privacy">Profile Visibility</Label>
                  <Select value={profileData.profile_privacy} onValueChange={(value) => handleInputChange('profile_privacy', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public - Visible to everyone</SelectItem>
                      <SelectItem value="tribe_only">Tribe Only - Verified tribal members</SelectItem>
                      <SelectItem value="private">Private - Only you</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="show_genealogy">Show Genealogy Information</Label>
                    <input
                      type="checkbox"
                      id="show_genealogy"
                      checked={profileData.show_genealogy}
                      onChange={(e) => handleInputChange('show_genealogy', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="show_contact_info">Show Contact Information</Label>
                    <input
                      type="checkbox"
                      id="show_contact_info"
                      checked={profileData.show_contact_info}
                      onChange={(e) => handleInputChange('show_contact_info', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="allow_messages">Allow Messages</Label>
                    <input
                      type="checkbox"
                      id="allow_messages"
                      checked={profileData.allow_messages}
                      onChange={(e) => handleInputChange('allow_messages', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfileEdit;